// DEPENDENCIES
import { FormikProps, FormikValues } from "formik";
import * as Sentry from "@sentry/react";

// TYPES
import { DraftUUID, OnboardingType, Step, StepData } from "../../types";
import { AppDispatch, RootState, store } from "../../app/store";
import { InfoStepType } from "../../reducers/infoStepReducer";
import { AxiosError } from "axios";

// UTILS
import {
  checkIsEmailAvailable,
  completePhysicalSteps,
  contactBlacklistSupport,
  deleteDniImages,
  deleteLiveness,
  genericToastifyError,
  getDniFromCuit,
  getHeritageDocuments,
  getIdentityResumePoint,
  getUnfinishedIdentityResumePoint,
  getInfoStepData,
  syncRemoteDraftSteps,
} from "../../utils";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import { goToStep } from "../../app/utils";
import { onUpdateFormData } from "../../reducers/onboardingDataReducer";
import {
  deleteBusinessDraft,
  deleteIndividualDraft,
} from "../../connectors/connectors";
import { logAnalyticsAndHotjarEvent } from "../../main";
import { getPartnership } from "../../utils/companies";
import { renewExpiredToken } from "../shared/renewExpiredToken";

// COMPONENTS
import InteractiveInformation from "../../components/InteractiveInformation/InteractiveInformation";
import { getResumeStepData } from "../../components/TemplateInterpreter/utils";
import { getRemoteStepData } from "../../components/TemplateInterpreter/utils";

const infoStep = ({
  onResetDraft,
  oldAuth,
}: {
  //How the template requested the email code in step 1
  oldAuth?: boolean;
  onResetDraft?: ({
    dispatch,
    state,
    draftUUID,
    formik,
  }: {
    dispatch: AppDispatch;
    state: RootState;
    draftUUID: DraftUUID;
    formik: FormikProps<FormikValues>;
  }) => Promise<void>;
}): Step => ({
  step: "information-step",
  component: (dispatch, state, formik) => {
    if (!state || !dispatch || !formik)
      throw new Error("Error: state, dispatch or formik not found");

    const email = formik?.values?.email;
    if (email) Sentry.setUser({ email });

    const { draftUUID, stepData } = state.onboardingData;
    const { type: infoStep, pendingOnboardingType } = state.infoStep;
    const { onboardingType } = state.activeTemplate;

    const onPrimaryClick = async () => {
      try {
        if (infoStep === "BLACKLIST" || infoStep === "INVALID_PARAMS") {
          contactBlacklistSupport();
        } else {
          await onInfoStepPrimary(
            dispatch,
            formik,
            infoStep,
            stepData,
            oldAuth,
          );
        }
      } catch (error) {
        console.log(error);
        showToastMessage(genericToastifyError, {
          containerId: "informationAlert",
          type: "ERROR",
        });
      }
    };

    const onSecondaryClick = async () => {
      try {
        await onInfoStepSecondary(
          dispatch,
          formik,
          infoStep,
          draftUUID,
          onResetDraft,
          pendingOnboardingType,
          oldAuth,
        );
      } catch (error) {
        showToastMessage(genericToastifyError, {
          containerId: "informationAlert",
          type: "ERROR",
        });
      }
    };

    const { alertType, title, subtitle, primaryText, secondaryText } =
      getInfoStepData({
        infoStep,
        onboardingType,
        options: { pendingOnboardingType },
      });

    return (
      <InteractiveInformation
        alertType={alertType}
        title={title}
        subtitle={subtitle}
        primaryButton={{
          text: primaryText,
          onClick: onPrimaryClick,
        }}
        secondaryButton={{
          text: secondaryText,
          onClick: onSecondaryClick,
        }}
      />
    );
  },
  hideLayoutButtons: true,
  hideStepper: true,
  centeredLogo: true,
});

const onInfoStepPrimary = async (
  dispatch: AppDispatch,
  formik: FormikProps<FormikValues>,
  infoStep: InfoStepType,
  stepData: StepData,
  oldAuth?: boolean,
) => {
  const { resumeStep } = stepData;

  switch (infoStep) {
    case "ACTIVE_ACCOUNT":
      {
        if (await renewExpiredToken({ dispatch, formik, oldAuth })) break;

        const resumePoint = getIdentityResumePoint(
          dispatch,
          Number(resumeStep) || 1,
        );
        goToStep({
          dispatch,
          step: resumePoint.step,
          substep: resumePoint.substep ?? 1,
        });
      }
      break;
    case "REGISTERED_EMAIL":
      {
        const { onboardingType } = store.getState().activeTemplate;
        const isPhysicalTemplate =
          onboardingType === "PHYSICAL" && !getPartnership(formik?.values);

        goToStep({
          dispatch,
          step: isPhysicalTemplate ? 1 : Number(resumeStep) || 1,
          substep: 1,
        });
      }
      break;
    case "ONBOARDING_IN_PROGRESS":
      dispatch({ type: "reset-store" });
      formik.resetForm();
      goToStep({ dispatch, step: 1 });
      break;
    case "PENDING_REGISTRY_REMOTE":
    case "PENDING_REGISTRY_LOCAL": {
      if (await renewExpiredToken({ dispatch, formik, oldAuth })) break;

      const isOwnDraft =
        store.getState().activeTemplate.onboardingType !==
        "ADDITIONAL_COOWNER";
      const hasRemoteDraft =
        infoStep === "PENDING_REGISTRY_LOCAL" &&
        isOwnDraft &&
        (await syncRemoteDraftSteps(async (steps, remoteDraft) => {
          await completePhysicalSteps({
            dispatch,
            formik,
            steps,
            draftReferrer: remoteDraft.referrer,
          });

          const { draftUUID, formValues } = store.getState().onboardingData;
          const heritageFiles = await getHeritageDocuments(
            draftUUID,
            formValues?.cuit,
          );
          if (heritageFiles?.length) {
            formik.setFieldValue("heritageFiles", heritageFiles);
            dispatch(onUpdateFormData({ heritageFiles }));
          }
        }));

      const values = hasRemoteDraft
        ? { ...formik.values, ...store.getState().onboardingData.formValues }
        : formik.values;
      const cuit = values?.cuit;
      const idNumber = values?.identityDocument;

      if (idNumber) {
        await checkIsEmailAvailable({
          dispatch,
          idNumber,
        });
      }

      //When the user reaches step 3 (even if he does not complete the data) we should not take him to step 1 in case he refreshes the page.
      //So we save the identityDocument in the store to avoid this.
      if (cuit) {
        dispatch(
          onUpdateFormData({
            identityDocument: getDniFromCuit(cuit),
          }),
        );
      }

      const { reachedSteps, lastCompletedSubStep } = stepData;

      const identityResumePoint =
        infoStep === "PENDING_REGISTRY_REMOTE" &&
        getUnfinishedIdentityResumePoint(dispatch, 3);
      if (identityResumePoint) {
        goToStep({ dispatch, ...identityResumePoint });
        break;
      }

      const state = store.getState();
      const remoteStepData =
        infoStep === "PENDING_REGISTRY_REMOTE" || hasRemoteDraft
          ? getRemoteStepData({
              template: state.activeTemplate,
              state,
              values,
              dispatch,
            })
          : null;
      const localStepData =
        infoStep === "PENDING_REGISTRY_LOCAL"
          ? getResumeStepData({
              steps: state.activeTemplate?.steps,
              stepData,
              values,
              state,
            })
          : null;
      const resumeStepData = remoteStepData ?? localStepData;

      const nextStep =
        resumeStepData?.step ?? (reachedSteps?.[reachedSteps.length - 1] || 1);
      const nextSubStep =
        resumeStepData?.substep ?? (lastCompletedSubStep + 1 || 1);

      goToStep({ dispatch, step: nextStep, substep: nextSubStep });
      logAnalyticsAndHotjarEvent("onboarding_resumed");
      break;
    }
    case null:
      goToStep({ dispatch, step: 1 });
      break;
    default:
      break;
  }
};

const onInfoStepSecondary = async (
  dispatch: AppDispatch,
  formik: FormikProps<FormikValues>,
  infoStep: InfoStepType,
  draftUUID: DraftUUID,
  onResetDraft?: ({
    dispatch,
    state,
    draftUUID,
    formik,
  }: {
    dispatch: AppDispatch;
    state: RootState;
    draftUUID: DraftUUID;
    formik: FormikProps<FormikValues>;
  }) => Promise<void>,
  pendingOnboardingType?: OnboardingType,
  oldAuth?: boolean,
) => {
  switch (infoStep) {
    case "PENDING_REGISTRY_REMOTE":
    case "PENDING_REGISTRY_LOCAL":
    case "ONBOARDING_IN_PROGRESS":
      {
        if (await renewExpiredToken({ dispatch, formik, oldAuth })) break;

        if (onResetDraft) {
          const state = store.getState();
          return await onResetDraft({ dispatch, state, draftUUID, formik });
        }

        try {
          if (pendingOnboardingType === "JURIDICAL") {
            await deleteBusinessDraft({ draftUUID });
          } else {
            await deleteIndividualDraft({ draftUUID });
          }
          await deleteDniImages({ uuid: draftUUID });
          await deleteLiveness({ uuid: draftUUID });
        } catch (error) {
          if (
            !(error instanceof AxiosError) ||
            error.response?.status !== 404
          ) {
            throw error;
          }
        }

        dispatch({ type: "reset-store" });
        formik.resetForm();
        goToStep({ dispatch, step: 1 });
        logAnalyticsAndHotjarEvent("onboarding_restart");
      }
      break;
    case null:
    case "BLACKLIST":
      goToStep({ dispatch, step: 1 });
      break;
    case "DRAFT_NOT_FOUND":
      dispatch({ type: "reset-store" });
      formik.resetForm();
      break;
    default:
      break;
  }
};

export default infoStep;
